import { useTranslation } from 'react-i18next';
import {
  DEFAULT_TABLE_PAGE_SIZE,
  TABLE_PAGE_SIZE_OPTIONS,
} from '@/components/shared/tablePaginationConstants';

export interface TablePaginationBarProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: readonly number[];
  disabled?: boolean;
}

function getPagesToShow(page: number, totalPages: number): number[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (page <= 3) {
    return [1, 2, 3, 4, 5];
  }
  if (page >= totalPages - 2) {
    return Array.from({ length: 5 }, (_, i) => totalPages - 4 + i);
  }
  return [page - 2, page - 1, page, page + 1, page + 2];
}

export function TablePaginationBar({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = TABLE_PAGE_SIZE_OPTIONS,
  disabled = false,
}: TablePaginationBarProps) {
  const { t } = useTranslation();

  if (totalItems <= 0) return null;

  const safePage = Math.min(Math.max(currentPage, 1), Math.max(totalPages, 1));
  const from = (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, totalItems);
  const showPageNav = totalPages > 1;

  const handlePageSizeChange = (nextSize: number) => {
    if (nextSize === pageSize || !onPageSizeChange) return;
    onPageSizeChange(nextSize);
  };

  return (
    <div className="flex flex-col gap-2 mt-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-xs text-gray-700">
        {t('pagination.showing')} {from} {t('pagination.to')} {to} {t('pagination.of')} {totalItems}
      </span>
      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        {onPageSizeChange ? (
          <label className="flex items-center gap-1.5 text-xs text-gray-700">
            <span className="whitespace-nowrap">{t('pagination.rowsPerPage')}</span>
            <select
              value={pageSize}
              disabled={disabled}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800 focus:border-[#1681ba] focus:outline-none focus:ring-1 focus:ring-[#1681ba] disabled:opacity-50"
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
              {!pageSizeOptions.some((option) => option === pageSize) ? (
                <option value={pageSize}>{pageSize}</option>
              ) : null}
            </select>
          </label>
        ) : null}
        {showPageNav ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange(1)}
              disabled={disabled || safePage === 1}
              className="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('pagination.first')}
            </button>
            <button
              type="button"
              onClick={() => onPageChange(Math.max(safePage - 1, 1))}
              disabled={disabled || safePage === 1}
              className="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('pagination.previous')}
            </button>
            {getPagesToShow(safePage, totalPages).map((pageNum) => (
              <button
                key={pageNum}
                type="button"
                onClick={() => onPageChange(pageNum)}
                disabled={disabled}
                className={`px-2 py-1 text-xs font-medium rounded ${
                  safePage === pageNum
                    ? 'bg-[#1681ba] text-white border-[#1681ba]'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {pageNum}
              </button>
            ))}
            <button
              type="button"
              onClick={() => onPageChange(Math.min(safePage + 1, totalPages))}
              disabled={disabled || safePage === totalPages}
              className="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('pagination.next')}
            </button>
            <button
              type="button"
              onClick={() => onPageChange(totalPages)}
              disabled={disabled || safePage === totalPages}
              className="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('pagination.last')}
            </button>
            <span className="text-xs text-gray-500 ml-2">
              {t('pagination.page')} {safePage} {t('pagination.of')} {totalPages}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { DEFAULT_TABLE_PAGE_SIZE, TABLE_PAGE_SIZE_OPTIONS };
